import { Component, inject, computed, ChangeDetectionStrategy } from "@angular/core";
import { ReactiveFormsModule, FormGroupDirective, ControlContainer } from "@angular/forms";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CardModalService } from "@shared/card-modal";
import type { IiFirmRole } from "@interfaces/index";
import { AssigneesPanel } from "../assignees-panel.class";
import { UsersService, compareByActiveAndName, WkTranslatePipe } from "@shared/index";
import type { ICardUserCardModel } from "@app/views-interfaces";
import { JobsJobAccessField } from "@shared/interfaces";
import { SharedModule } from "@shared/shared.module";

@Component({
    selector: "wk-assignees-jobs-panel",
    templateUrl: "./assignees-jobs-panel.component.html",
    styleUrl: "../assignees-panel.scss",
    host: { class: "wk-cm__card-panel__assignees" },
    imports: [TranslateModule, SharedModule, JumpstartComponentsModule, ReactiveFormsModule],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [WkTranslatePipe],
})
export class AssigneesJobsPanelComponent extends AssigneesPanel {
    protected readonly cardModalService = inject(CardModalService);
    private readonly usersService = inject(UsersService);
    private readonly wkTranslate = inject(WkTranslatePipe);
    private readonly translateService = inject(TranslateService);

    protected readonly isRoleDisabled = computed(() => {
        const editDisabled = !this.cardModalService.canEditAssignee();
        const type = this.type();
        const fieldByRole = this.fieldByRole();
        return (role: IiFirmRole | string) => {
            switch (type) {
                case this.CardPanelType.Job: {
                    const field = typeof role === "string" ? role : fieldByRole.get(role.Id);
                    const acessField = JobsJobAccessField[field as keyof typeof JobsJobAccessField];
                    return !this.jobsAndBillingCommonService.hasAccess(acessField);
                }
                case this.CardPanelType.Contact:
                    if (typeof role === "string") return false;
                    return (role.IsManager || role.IsPartner) && editDisabled;
                default:
                    return false;
            }
        };
    });

    readonly usersPerRole = computed(() => {
        const defaultUser: ICardUserCardModel = {
            id: null,
            iFirmUserId: null,
            label: "General.None",
            active: true,
        };

        const type = this.type();
        const rolesWithUsers = Array.from(this.iFirmRolesService.rolesWithUsers()?.values() ?? []) ?? [];
        const fieldByRole = this.fieldByRole();
        const users = Array.from(this.usersService.usersById()?.fromUserId?.values() ?? []);
        if (!rolesWithUsers?.length || !fieldByRole?.size) return new Map();

        const map = new Map<string, ICardUserCardModel[]>();

        for (const role of rolesWithUsers) {
            const roleField = fieldByRole.get(role.Id);
            if (!roleField) continue;

            const userId = this.cardModalService.cardModalFormGroup()?.value?.[roleField] ?? "";

            const users = Array.from(role.Users.values())
                .filter((u) => u.Active || u.Id === userId || u.IFirmUserId === userId)
                .sort(compareByActiveAndName)
                .map((u) => ({
                    id: u.Id,
                    iFirmUserId: u.IFirmUserId,
                    label: u.FullName,
                    active: u.Active,
                })) as ICardUserCardModel[];
            users.unshift(defaultUser);

            map.set(role.Id, users);
        }

        if (type === this.CardPanelType.Job && users?.length) {
            const jobOwnerField = "jobOwner";
            const jobOwnerUserId = this.cardModalService.cardModalFormGroup()?.value?.jobOwner ?? "";
            const jobOwnerUsers = users
                .filter((user) => user.Active || user.Id === jobOwnerUserId || user.IFirmUserId === jobOwnerUserId)
                .sort(compareByActiveAndName)
                .map((user) => ({
                    id: user.Id,
                    iFirmUserId: user.IFirmUserId,
                    label: user.FullName,
                    active: user.Active,
                })) as ICardUserCardModel[];
            map.set(jobOwnerField, jobOwnerUsers);

            const jobCurrentlyResponsibleField = "currentlyResponsible";
            const jobCurrentlyResponsibleUserId = this.cardModalService.cardModalFormGroup()?.value?.currentlyResponsible ?? "";
            const jobCurrentlyResponsibleUsers = users
                .filter((user) => user.Active || user.Id === jobCurrentlyResponsibleUserId || user.IFirmUserId === jobCurrentlyResponsibleUserId)
                .sort(compareByActiveAndName)
                .map((user) => ({
                    id: user.Id,
                    iFirmUserId: user.IFirmUserId,
                    label: user.FullName,
                    active: user.Active,
                })) as ICardUserCardModel[];
            jobCurrentlyResponsibleUsers.unshift(defaultUser);
            map.set(jobCurrentlyResponsibleField, jobCurrentlyResponsibleUsers);
        }

        return map;
    });

    protected readonly labelByRole = computed(() => {
        const type = this.type();
        const roles = this.iFirmRolesService.sIFirmRoles();
        const map = new Map<string, string>();
        const fieldByRole = this.fieldByRole();
        if (!type) return map;

        for (const role of roles) {
            if (!role.IsManager && !role.IsPartner) continue;

            let label = this.wkTranslate.transform(role.Description);
            if (type === this.CardPanelType.Job) {
                label = this.translateService.instant("JobsAndBilling.Job") + " " + label;
            }
            if (!label) continue;

            const labelKey = fieldByRole.get(role.Id);
            if (!labelKey) continue;
            map.set(labelKey, label);
        }

        return map;
    });

    protected readonly fieldByRole = computed(() => {
        const type = this.type();
        const roles = this.iFirmRolesService.sIFirmRoles();
        const map = new Map<string, string>();
        if (!roles || !type) return map;

        for (const role of roles) {
            if (!role.IsManager && !role.IsPartner) continue;
            const roleField = (type === this.CardPanelType.Job ? "job" : "contact") + (role.IsManager ? "Manager" : "Partner");
            map.set(role.Id, roleField);
        }

        return map;
    });
}
